import { type Moment } from "moment";
import { Component, type CSSProperties } from "react";

import { CommonConstants } from "types/constants";
import { BTLoginTypes, HoSubAck } from "types/enum";

import { formattedPlural } from "utilities/string/string";

import {
    BTIconHomeowners,
    BTIconInternalUsers,
    BTIconSubs,
} from "commonComponents/btWrappers/BTIcon";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";

import { HoSubAckDisplay } from "entity/warranty/common/HoSubAckDisplay/HoSubAckDisplay";

import "./WarrantyServiceStatus.less";

enum AppointmentDateState {
    overTheshold,
    withinThreshold,
    withinDay,
    expired,
}

interface IOwnerWarrantyServiceStatusProps {
    assignedToLoginType?: undefined;
    forOwner: true;
}
interface ISubsWarrantyServiceStatusProps {
    assignedToLoginType: BTLoginTypes | null;
    forOwner?: false;
}

interface IWarrantyServiceStatusBaseProps {
    status: HoSubAck;
    scheduledDateAccepted: boolean;
    acknowledgedDate?: Moment;
    hoursUntilAppointmentDate: number;
}

export type IWarrantyServiceStatusProps = IWarrantyServiceStatusBaseProps &
    (IOwnerWarrantyServiceStatusProps | ISubsWarrantyServiceStatusProps);

export class WarrantyServiceStatus extends Component<IWarrantyServiceStatusProps> {
    static defaultProps = {
        forOwner: false,
    };

    private get acknowledgedStatus() {
        const { forOwner, status } = this.props;
        return forOwner && status === HoSubAck.InternallyAccepted
            ? HoSubAck.BuilderApproved
            : status;
    }

    private get appointmentDateState(): AppointmentDateState | null {
        const { hoursUntilAppointmentDate } = this.props;

        const hoursInDay = 24;
        const warningThresholdDays = 3;
        if (hoursUntilAppointmentDate > warningThresholdDays * hoursInDay) {
            return AppointmentDateState.overTheshold;
        } else if (hoursUntilAppointmentDate > hoursInDay) {
            return AppointmentDateState.withinThreshold;
        } else if (hoursUntilAppointmentDate > 0) {
            return AppointmentDateState.withinDay;
        } else {
            return AppointmentDateState.expired;
        }
    }

    private get statusClass() {
        const { status } = this.props;
        const dateState = this.appointmentDateState;

        let statusClass = HoSubAck[status];
        if (status === HoSubAck.Pending) {
            if (dateState === AppointmentDateState.overTheshold) {
                statusClass += " over-threshold";
            } else if (dateState === AppointmentDateState.withinThreshold) {
                statusClass += " within-threshold";
            } else if (dateState === AppointmentDateState.withinDay) {
                statusClass += " within-day";
            } else if (dateState === AppointmentDateState.expired) {
                statusClass += " expired";
            }
        }

        return statusClass;
    }

    private getPendingRemainingTimeMessage(): string | null {
        const { status, hoursUntilAppointmentDate } = this.props;

        let result: string | null = null;

        if (status === HoSubAck.Pending) {
            const daysRemaining = Math.floor(hoursUntilAppointmentDate / 24);
            if (daysRemaining >= 1) {
                const dayOrDays = formattedPlural({
                    value: daysRemaining,
                    one: "day",
                    other: "days",
                });
                result = !this.props.scheduledDateAccepted
                    ? "TBD"
                    : `${daysRemaining} ${dayOrDays} remaining`;
            } else if (daysRemaining === 0) {
                result = "Less than 1 day!";
            }
        }

        return result && ` - ${result}`;
    }

    render() {
        let icon;

        const pastDue = this.appointmentDateState === AppointmentDateState.expired;

        const statusImage: CSSProperties = {
            width: "15px",
            marginRight: ".5em",
        };

        if (this.props.forOwner) {
            icon = <BTIconHomeowners title="Client" style={statusImage} />;
        } else {
            icon =
                this.props.assignedToLoginType === BTLoginTypes.BUILDER ? (
                    <BTIconInternalUsers
                        title={CommonConstants.InternalUser.singular}
                        style={statusImage}
                    />
                ) : (
                    <BTIconSubs title={CommonConstants.Sub.singular} style={statusImage} />
                );
        }
        return (
            <div className="WarrantyServiceStatus">
                {icon}
                <HoSubAckDisplay
                    status={this.acknowledgedStatus}
                    pastDue={pastDue}
                    render={(statusText, overrideText) => (
                        <>
                            <span className={`status-text ${this.statusClass}`}>{statusText}</span>
                            {overrideText}
                        </>
                    )}
                />
                {this.getPendingRemainingTimeMessage()}
                {this.props.acknowledgedDate && (
                    <>
                        {" "}
                        - <DateDisplay showTime={true} value={this.props.acknowledgedDate} />
                    </>
                )}
            </div>
        );
    }
}
